import React from 'react';
import {DEFAULT_PAGE_SIZE_OPTIONS} from '../hooks/usePageSize';

interface PageSizeSelectProps {
    /** Текущий размер страницы. */
    value: number;
    onChange: (size: number) => void;
    /** Варианты; по умолчанию — общий набор 20 / 50 / 100. */
    options?: number[];
    /**
     * Имя таблицы для локатора и идентификатора поля: `page-size-<table>`.
     * По конвенции styleguide §Testability `id` и `data-testid` у поля совпадают.
     */
    table: string;
    /** Подпись слева от поля: «Вещей на странице», «Записей на странице». */
    label?: string;
    /**
     * Сколько всего элементов в выдаче. Если их меньше наименьшего варианта, выбирать
     * нечего — переключатель не рисуем, чтобы не предлагать бессмысленное действие.
     * `undefined` — показываем всегда (число неизвестно).
     */
    totalElements?: number;
    className?: string;
}

/**
 * Выбор количества элементов на странице — один компонент на все таблицы и списки (CTHS-7).
 *
 * <p>Раньше он существовал только в каталоге гардероба, а в аудите стоял безымянный `select`
 * без подписи и без сохранения выбора. Разные реализации одного и того же решения расходятся
 * при первой же правке, поэтому здесь и подпись, и локатор, и набор вариантов заданы в одном
 * месте; сохранение выбора живёт в {@link usePageSize}.</p>
 */
const PageSizeSelect: React.FC<PageSizeSelectProps> = ({
    value,
    onChange,
    options = DEFAULT_PAGE_SIZE_OPTIONS,
    table,
    label = 'На странице',
    totalElements,
    className = '',
}) => {
    if (totalElements !== undefined && totalElements <= Math.min(...options)) {
        return null;
    }

    const id = `page-size-${table}`;

    return (
        <div className={`flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 ${className}`}>
            <label htmlFor={id} className="whitespace-nowrap">{label}</label>
            <select
                id={id}
                data-testid={id}
                value={value}
                onChange={e => onChange(parseInt(e.target.value, 10))}
                className="min-h-[44px] sm:min-h-0 rounded-lg border border-gray-200 dark:border-white/10
                           bg-white dark:bg-ink-800 text-gray-700 dark:text-gray-200 px-2 py-1 text-xs
                           focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-400
                           transition-[border-color,box-shadow] duration-ui ease-out"
            >
                {options.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
        </div>
    );
};

export default PageSizeSelect;
